import { AssetsTotal } from "../../graphql/queries/assetsTotal";
import { useQuery } from "@apollo/client";
import _ from "lodash";
import { CircularProgress, Typography } from "@mui/joy";
import { t } from "i18next";
import { WidgetConfig } from "../DashboardWidgetConfig";
import WidgetHeader from "../WidgetHeader";
import { useNavigate } from "react-router-dom";

export type AssetMetricType = 'harshCondition' | 'maintenanceDueSoon' | 'maintenanceOverdue' | 'exhaustFilter' | 'missingSensor' | 'vsdCandidate' | 'sensorValueOutOfRange';

interface AssetMetricDefinition {
  title: string;
  subtitle?: string;
  variables: Record<string, unknown>;
}

const ASSET_METRIC_DEFINITIONS: Record<AssetMetricType, AssetMetricDefinition> = {
  harshCondition: {
    title: 'Harsh Condition',
    variables: { filter: { assetMetrics: { harshCondition: { in: ["GOOD", "MEDIUM", "POOR"] } } } },
  },
  maintenanceDueSoon: {
    title: 'Maintenance Due',
    variables: { filter: { assetMetrics: { maintenanceDueSoon: { equals: true } } } },
  },
  maintenanceOverdue: {
    title: 'Maintenance Overdue',
    variables: { filter: { assetMetrics: { maintenanceOverdue: { equals: true } } } },
  },
  exhaustFilter: {
    title: 'Exhaust Filter',
    variables: { filter: { assetMetrics: { exhaustFilterCondition: { in: ["GOOD", "MEDIUM", "POOR"] } } } },
  },
  missingSensor: {
    title: 'Missing Sensor',
    variables: { filter: { assetMetrics: { missingSensorValues: { equals: true } } } },
  },
  vsdCandidate: {
    title: 'VSD Candidate',
    variables: { filter: { assetMetrics: { vsdCandidate: { in: ["GOOD", "MEDIUM", "POOR"] } } } },
  },
  sensorValueOutOfRange: {
    title: 'Sensor value',
    subtitle: 'out of range',
    variables: { filter: { assetMetrics: { sensorValueOutOfRange: { equals: true } } } },
  },
};

interface AssetMetricsWidgetProps {
  onDelete: () => void;
  onEdit: () => void;
  config?: WidgetConfig;
}

const AssetMetricsWidget: React.FC<AssetMetricsWidgetProps> = ({ onDelete, onEdit, config }) => {
  const metricType = (config?.assetMetricType as AssetMetricType) || 'harshCondition';
  const definition = ASSET_METRIC_DEFINITIONS[metricType];
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(AssetsTotal, {
    variables: definition.variables,
  });

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress size="sm" />
      </div>
    );
  }

  if (error) {
    return <p style={{ fontSize: "0.875rem", color: "#d32f2f" }}>Error loading data.</p>;
  }

  const value = _.get(data, 'searchAssets.total', 0);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <WidgetHeader
        title={t(definition.title)}
        onDelete={onDelete}
        onEdit={onEdit}
      />

      <div
        className="no-drag"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          flex: 1,
          paddingLeft: "10px",
          cursor: "pointer",
        }}
        onClick={() => navigate(`/assets?${metricType}=true`)}
      >
        <Typography level="titleExtraLarge" sx={{ color: "#333", fontWeight: 700 }}>
          {value}
        </Typography>
        {definition.subtitle && (
          <Typography level="bodySmall" sx={{ color: "#666" }}>
            {t(definition.subtitle)}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default AssetMetricsWidget;
